import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from './Navbar';
import Footer from './Footer';

const CurriculumHolisticoPage: React.FC = () => {
    const navigate = useNavigate();
    const { language } = useLanguage();

    const t = (es: string, en: string) => language === 'es' ? es : en;

    const handleThemeChange = (theme: number) => {
        const bookRoutes = {
            2: '/libros/1',
            3: '/libros/2', 
            4: '/libros/3',
            5: '/libros/4',
            6: '/libros/5',
            7: '/libros/6',
            8: '/libros/7',
            9: '/libros/8'
        };
        const route = bookRoutes[theme as keyof typeof bookRoutes];
        if (route) {
            window.location.href = route;
        }
    };

    const healthSections = [
        {
            id: 'mental',
            title: t('Salud Mental', 'Mental Health'),
            route: '/salud-mental',
            image: '/images/infosection/saludmental.png',
            color: 'from-blue-500 to-cyan-600'
        },
        {
            id: 'social',
            title: t('Salud Social', 'Social Health'),
            route: '/salud-social',
            image: '/images/infosection/saludsocial.png',
            color: 'from-green-500 to-emerald-600'
        },
        {
            id: 'fisica',
            title: t('Salud Física', 'Physical Health'),
            route: '/salud-fisica',
            image: '/images/infosection/saludfisica.png',
            color: 'from-orange-500 to-red-600'
        },
        {
            id: 'financiera',
            title: t('Salud Financiera', 'Financial Health'),
            route: '/salud-financiera',
            image: '/images/infosection/saludfinanciera.png',
            color: 'from-purple-500 to-pink-600'
        }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
            {/* Navbar */}
            <Navbar currentTheme={1} onThemeChange={handleThemeChange} />
            
            {/* Contenido principal */}
            <main className="pt-16">
                {/* Título principal */}
                <section className="py-8 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center">
                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
                                {t('Currículum Holístico', 'Holistic Curriculum')}
                            </h1>
                            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                                {t('Desarrollo integral en todas las dimensiones humanas para un crecimiento equilibrado', 'Integral development in all human dimensions for balanced growth')}
                            </p>
                        </div>
                    </div>
                </section>

                {/* Secciones de salud una debajo de la otra */}
                {healthSections.map((section, index) => (
                    <section key={section.id} className="min-h-screen relative">
                        {/* Imagen de fondo */}
                        <div className="absolute inset-0 w-full h-full">
                            <img 
                                src={section.image}
                                alt={section.title}
                                className="w-full h-full object-cover object-center"
                            />
                        </div>
                        
                        {/* Contenido centrado */}
                        <div className="relative z-10 flex items-center justify-center min-h-screen">
                            <div className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center ${section.id === 'mental' ? 'transform translate-x-44' : ''} ${section.id === 'social' ? 'transform -translate-x-44' : ''} ${section.id === 'fisica' ? 'transform translate-x-72' : ''} ${section.id === 'financiera' ? 'transform -translate-x-56' : ''}`}>
                                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-8">
                                    {section.title}
                                </h2>
                                
                                <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 sm:p-12 shadow-2xl">
                                    <p className="text-xl sm:text-2xl text-gray-800 leading-relaxed mb-8">
                                        {section.id === 'mental' && t(
                                            'Desarrollo del pensamiento crítico, inteligencia emocional y bienestar mental. Aprende a gestionar emociones, desarrollar resiliencia y mantener un estado mental saludable.',
                                            'Development of critical thinking, emotional intelligence and mental well-being. Learn to manage emotions, develop resilience and maintain a healthy mental state.'
                                        )}
                                        {section.id === 'social' && t(
                                            'Habilidades de comunicación, liderazgo y competencias profesionales. Desarrolla relaciones saludables, trabajo en equipo y habilidades sociales efectivas.',
                                            'Communication skills, leadership and professional competencies. Develop healthy relationships, teamwork and effective social skills.'
                                        )}
                                        {section.id === 'fisica' && t(
                                            'Bienestar físico, nutrición y desarrollo biológico saludable. Mantén un cuerpo fuerte, energético y en óptimas condiciones.',
                                            'Physical well-being, nutrition and healthy biological development. Maintain a strong, energetic body in optimal condition.'
                                        )}
                                        {section.id === 'financiera' && t(
                                            'Inteligencia financiera, ahorro y gestión responsable del dinero. Aprende a manejar recursos, planificar y crear riqueza de manera sostenible.',
                                            'Financial intelligence, saving and responsible money management. Learn to manage resources, plan and create wealth sustainably.'
                                        )}
                                    </p>
                                    
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                                        {section.id === 'mental' && (
                                            <>
                                                <div className="bg-blue-50 rounded-xl p-6">
                                                    <h3 className="text-lg font-bold text-blue-800 mb-2">{t('Pensamiento Crítico', 'Critical Thinking')}</h3>
                                                    <p className="text-blue-700 text-sm">{t('Desarrolla análisis y razonamiento', 'Develop analysis and reasoning')}</p>
                                                </div>
                                                <div className="bg-blue-50 rounded-xl p-6">
                                                    <h3 className="text-lg font-bold text-blue-800 mb-2">{t('Gestión Emocional', 'Emotional Management')}</h3>
                                                    <p className="text-blue-700 text-sm">{t('Controla y entiende tus emociones', 'Control and understand your emotions')}</p>
                                                </div>
                                                <div className="bg-blue-50 rounded-xl p-6">
                                                    <h3 className="text-lg font-bold text-blue-800 mb-2">{t('Resiliencia', 'Resilience')}</h3>
                                                    <p className="text-blue-700 text-sm">{t('Adaptabilidad ante desafíos', 'Adaptability to challenges')}</p>
                                                </div>
                                            </>
                                        )}
                                        {section.id === 'social' && (
                                            <>
                                                <div className="bg-green-50 rounded-xl p-6">
                                                    <h3 className="text-lg font-bold text-green-800 mb-2">{t('Comunicación', 'Communication')}</h3>
                                                    <p className="text-green-700 text-sm">{t('Expresa ideas efectivamente', 'Express ideas effectively')}</p>
                                                </div>
                                                <div className="bg-green-50 rounded-xl p-6">
                                                    <h3 className="text-lg font-bold text-green-800 mb-2">{t('Trabajo en Equipo', 'Teamwork')}</h3>
                                                    <p className="text-green-700 text-sm">{t('Colabora y coopera', 'Collaborate and cooperate')}</p>
                                                </div>
                                                <div className="bg-green-50 rounded-xl p-6">
                                                    <h3 className="text-lg font-bold text-green-800 mb-2">{t('Liderazgo', 'Leadership')}</h3>
                                                    <p className="text-green-700 text-sm">{t('Guía y motiva a otros', 'Guide and motivate others')}</p>
                                                </div>
                                            </>
                                        )}
                                        {section.id === 'fisica' && (
                                            <>
                                                <div className="bg-orange-50 rounded-xl p-6">
                                                    <h3 className="text-lg font-bold text-orange-800 mb-2">{t('Fuerza Física', 'Physical Strength')}</h3>
                                                    <p className="text-orange-700 text-sm">{t('Desarrolla resistencia y vigor', 'Develop endurance and vigor')}</p>
                                                </div>
                                                <div className="bg-orange-50 rounded-xl p-6">
                                                    <h3 className="text-lg font-bold text-orange-800 mb-2">{t('Nutrición', 'Nutrition')}</h3>
                                                    <p className="text-orange-700 text-sm">{t('Alimentación balanceada', 'Balanced nutrition')}</p>
                                                </div>
                                                <div className="bg-orange-50 rounded-xl p-6">
                                                    <h3 className="text-lg font-bold text-orange-800 mb-2">{t('Hábitos Saludables', 'Healthy Habits')}</h3>
                                                    <p className="text-orange-700 text-sm">{t('Rutinas de bienestar', 'Wellness routines')}</p>
                                                </div>
                                            </>
                                        )}
                                        {section.id === 'financiera' && (
                                            <>
                                                <div className="bg-purple-50 rounded-xl p-6">
                                                    <h3 className="text-lg font-bold text-purple-800 mb-2">{t('Gestión del Dinero', 'Money Management')}</h3>
                                                    <p className="text-purple-700 text-sm">{t('Administra recursos sabiamente', 'Manage resources wisely')}</p>
                                                </div>
                                                <div className="bg-purple-50 rounded-xl p-6">
                                                    <h3 className="text-lg font-bold text-purple-800 mb-2">{t('Planificación', 'Planning')}</h3>
                                                    <p className="text-purple-700 text-sm">{t('Estrategias financieras', 'Financial strategies')}</p>
                                                </div>
                                                <div className="bg-purple-50 rounded-xl p-6">
                                                    <h3 className="text-lg font-bold text-purple-800 mb-2">{t('Inversión', 'Investment')}</h3>
                                                    <p className="text-purple-700 text-sm">{t('Crecimiento de capital', 'Capital growth')}</p>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                ))}
            </main>

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default CurriculumHolisticoPage; 